import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { LocationDetails } from '../../data-objects/location-details';
import { RefDataDropdownComponent } from '../ref-data-dropdown/ref-data-dropdown.component';
import { RefDataItem, RefDataType } from '../../data-objects/ref-data-item';
import { LoginService } from '../../services/login.service';
import { RefDataService } from '../../services/ref-data.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AddressService } from '../../services/address.service';
import { Subscription } from 'rxjs/Subscription';
import { TimerObservable } from 'rxjs/observable/TimerObservable';
import { AddressViewmodel } from '../../viewmodels/address-viewmodel';
import * as _ from 'lodash';

const postcodeRE = /^(0[289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2})$/;

@Component({
  selector: 'app-address-details',
  templateUrl: './address-details.component.html',
  styleUrls: ['./address-details.component.css']
})
export class AddressDetailsComponent implements OnInit, AfterViewInit {
  @ViewChild('focusInput') focusInputElement: ElementRef;
  @ViewChild('streetType') streetTypes: RefDataDropdownComponent;
  @ViewChild('streetSuffixType') streetSuffixTypes: RefDataDropdownComponent;
  @ViewChild('multipleAddress') multipleAddresses: RefDataDropdownComponent;
  @ViewChild('findProperty') findProperty: ElementRef;
  @Input()
  isReadOnly: boolean;
  @Input()
  isLand = false;
  @Input()
  showHeading = true;
  @Input()
  showSubHeading = true;
  @Input()
  streetType: string;
  @Input()
  streetSuffixType: string;
  @Input()
  addressViewModel: AddressViewmodel;
  @Output()
  onViewModelChange: EventEmitter<AddressViewmodel>;

  isRequesting: boolean;
  lotControlLength = 10;
  planControlLength = 25;
  streetNumberControlLength = 10;
  unitNumberControlLength = 10;
  streetNameControlLength = 50;
  suburbControlLength = 50;
  postcodeControlLength = 4;
  maxNumberOfAddresses = 100;

  unitNumberControl: FormControl;
  lotControl: FormControl;
  planControl: FormControl;
  streetNumberControl: FormControl;
  streetNameControl: FormControl;
  suburbControl: FormControl;
  postcodeControl: FormControl;

  private addressGroup: FormGroup;

  private addressChangeSubscription: Subscription;
  private timerSubscription: Subscription;

  //#REGION: INITIALIZERS
  constructor(private loginService: LoginService, private refDataService: RefDataService, private addressService: AddressService) {
    this.onViewModelChange = new EventEmitter<AddressViewmodel>();
    this.lotControl = new FormControl('', [Validators.maxLength(this.lotControlLength)]);
    this.planControl = new FormControl('', [Validators.maxLength(this.planControlLength)]);
    this.streetNumberControl = new FormControl('', [Validators.maxLength(this.streetNumberControlLength)]);
    this.unitNumberControl = new FormControl('', [Validators.maxLength(this.unitNumberControlLength)]);
    this.streetNameControl = new FormControl('', [Validators.maxLength(this.streetNameControlLength)]);
    this.suburbControl = new FormControl('', [Validators.maxLength(this.suburbControlLength)]);
    this.postcodeControl = new FormControl('', [Validators.compose([
      Validators.maxLength(this.postcodeControlLength),
      Validators.pattern(postcodeRE)
    ])]);

    this.addressGroup = new FormGroup({
      lot: this.lotControl,
      plan: this.planControl,
      unitNumber: this.unitNumberControl,
      streetNumber: this.streetNumberControl,
      streetName: this.streetNameControl,
      suburb: this.suburbControl,
      postcode: this.postcodeControl
    });
  }

  ngOnInit() {
    const pleaseSelect: RefDataItem[] = [new RefDataItem(0, 'Please Select...')];
    this.refDataService.retrieveReferenceData(RefDataType.StreetTypes, this.loginService.getToken()).subscribe(
      (refDataItems) => {
        if (this.streetTypes) {
          this.streetTypes.items = pleaseSelect.concat(refDataItems);
          if (this.addressViewModel && this.addressViewModel.addressDetails) {
            this.streetTypes.selectedItemId = (this.addressViewModel.addressDetails.streetTypeId)
                                                ? this.addressViewModel.addressDetails.streetTypeId : 0;

            if (this.addressViewModel.multipleAddressDetails && this.addressViewModel.multipleAddressDetails.length > 1) {
              this.multipleAddresses.items = AddressDetailsComponent.addressesToDataItems(this.addressViewModel.multipleAddressDetails);
              this.multipleAddresses.selectedItemId = _.findIndex(this.addressViewModel.multipleAddressDetails,
                (addressItem: LocationDetails) => addressItem.installationNumber === this.addressViewModel.addressDetails.installationNumber);
              this.addressValidated = true;
            } else {
              this.multipleAddresses.selectedItemId = 0;
              this.addressValidated = true;
            }
          }
        }
      },
      err => {
        this.errorMessage = 'Server error';
      });

    const streetSuffix: RefDataItem[] = [new RefDataItem(0, 'Please Select...')];
    this.refDataService.retrieveReferenceData(RefDataType.StreetSuffixes, this.loginService.getToken()).subscribe(
      (refDataItems) => {
        if (this.streetSuffixTypes) {          
          this.streetSuffixTypes.items = streetSuffix.concat(refDataItems);
          if (this.addressViewModel && this.addressViewModel.addressDetails) {
            this.streetSuffixTypes.selectedItemId = (this.addressViewModel.addressDetails.streetSuffixTypeId)
                                                      ? this.addressViewModel.addressDetails.streetSuffixTypeId : 0;

            // remove after testing
            // if (this.addressViewModel.multipleAddressDetails && this.addressViewModel.multipleAddressDetails.length > 1) {
            //   this.multipleAddresses.items = AddressDetailsComponent.addressesToDataItems(this.addressViewModel.multipleAddressDetails);
            //   this.multipleAddresses.selectedItemId = _.findIndex(this.addressViewModel.multipleAddressDetails,
            //     (addressItem: LocationDetails) => addressItem.installationNumber === this.addressViewModel.addressDetails.installationNumber);
            //   this.addressValidated = true;
            // } else {
            //   this.multipleAddresses.selectedItemId = 0;
            //   this.addressValidated = true;
            // }
          }
        }
      }, err => {
        this.errorMessage = 'Server error';
      }
    );
    this.subscribeToAddressChanges();
  }

  ngAfterViewInit(): void {
    this.setFocusOnFirstElement();
  }

  // #REGION: PRIVATE METHODS
  private static addressesToDataItems(addresses: LocationDetails[]): RefDataItem[] {
    const retVal: RefDataItem[] = [];
    retVal.push(new RefDataItem(0, 'Please Select...'));
    for (let i = 0; i < addresses.length; i++) {
      retVal.push(new RefDataItem(i + 1, addresses[i].locationAddress));
    }
    return retVal;
  }

  private subscribeToAddressChanges(): void {
    const addressChanges$ = this.addressGroup.valueChanges;
    this.addressChangeSubscription = addressChanges$.subscribe(values => {
      if (!values) {
        return;
      }
      this.addressValidated = false;
      let atLeastOneValueSet = false;
      for (const v in values) {
        if (values.hasOwnProperty(v) && v && values[v]) {
          atLeastOneValueSet = true;
        }
      }
      let addressValid = false;
      if (!!values.lot && !!values.plan) {
        addressValid = true;
      } else if (!!values.streetNumber && !!values.streetName && (!!values.suburb || !!values.postcode)) {
        addressValid = true;
      }
      this.addressInvalid = !addressValid || this.addressGroup.invalid;
      this.addressHasValues = atLeastOneValueSet;
      this.addressHasPlan = !!values.plan;
      this.addressHasStreetName = !!values.streetName;
      this.addressHasStreetNo = !!values.streetNumber;
      this.addressHasStreetType = !!values.streetType;
      this.addressHasSuburb = !!values.suburb;

    });
  }

  private setValidatedAddress(address: LocationDetails) {
    this.isRequesting = true;

    // set pricing zone prop in address service if it exists
    this.addressService.setPricingZone(address.pricingZone ? address.pricingZone : 'FMR CWW');

    this.addressService.validateProperty(address.installationNumber, this.loginService.getToken()).subscribe(() => {
      this.isRequesting = false;
      this.errorMessage = null;
      // populate address values
      this.addressDetails.copy(address);
      this.streetTypes.selectedItemId = address.streetTypeId;

      const timer = TimerObservable.create(500);
      this.timerSubscription = timer.subscribe(t => {
        this.addressValidated = true;
        this.timerSubscription.unsubscribe();
      });
    },
      err => { // Report errors if any
        this.isRequesting = false;
        this.addressValidated = false;
        this.isRequesting = false;
        if (err.status === 404) {
          this.errorMessage = err.message.err;
        } else if (err.status === 0 && err.statusText === 'Unknown Error') {
          this.errorMessage = 'Application is unavailable';
        } else {
          this.errorMessage = 'Server error';
        }
      });
  }

  //#REGION: PUBLIC METHODS
  disableControls(v: boolean) {
    if (v) {
      this.unitNumberControl.disable();
      this.lotControl.disable();
      this.planControl.disable();
      this.streetNumberControl.disable();
      this.streetNameControl.disable();
      this.suburbControl.disable();
      this.postcodeControl.disable();
      this.streetTypes.disableControl(true);
      this.streetTypes.selectedItemId = 0;
      if (!this.isLand) {
        this.findProperty.nativeElement.style.display = 'none';
      }
      this.addressViewModel.clear();
      this.multipleAddressDetails = null;
      this.addressDetails.streetTypeId = 0;
    } else {
      this.unitNumberControl.enable();
      this.lotControl.enable();
      this.planControl.enable();
      this.streetNumberControl.enable();
      this.streetNameControl.enable();
      this.suburbControl.enable();
      this.postcodeControl.enable();
      this.streetTypes.disableControl(false);
      if (!this.isLand) {
        this.findProperty.nativeElement.style.display = 'block';
      }
    }
  }

  handleStreetTypeChanged($event) {
    const selectedItem: RefDataItem = this.streetTypes.selectedItem;
    if (selectedItem) {
      this.addressDetails.streetType = selectedItem.display;
      if (typeof selectedItem.id === 'number' || typeof selectedItem.id === 'string') {
        this.addressDetails.streetTypeId = <number>(selectedItem.id);
        this.addressValidated = false;
      }
      this.onViewModelChange.emit(this.addressViewModel);
    }
  }

  handlestreetSuffixTypeChanged($event) {
    const selectedItem: RefDataItem = this.streetSuffixTypes.selectedItem;
    if (selectedItem) {
      this.addressDetails.streetSuffixType = selectedItem.display;
      if (typeof selectedItem.id === 'number' || typeof selectedItem.id === 'string') {
        this.addressDetails.streetSuffixTypeId = <number>(selectedItem.id);
        this.addressValidated = false;
      }
      this.onViewModelChange.emit(this.addressViewModel);
    }
  }

  handleSelectedAddressChanged($event) {
    const selectedItem: RefDataItem = this.multipleAddresses.selectedItem;
    if (selectedItem) {
      if (typeof selectedItem.id === 'number' && this.multipleAddressDetails && selectedItem.id <= this.multipleAddressDetails.length) {
        this.setValidatedAddress(this.multipleAddressDetails[selectedItem.id - 1]);
      }
      this.onViewModelChange.emit(this.addressViewModel);
    }
  }

  setFocusOnFirstElement(): void {
    this.focusInputElement.nativeElement.focus();
  }

  onModelChange() {
    this.onViewModelChange.emit(this.addressViewModel);
  }

  validateAddress() {
    if (this.isLand) {
      return;
    }
    if (!this.addressHasValues) {
      this.showErrorMessage = true;
      return;
    }
    if (!this.addressPopulated) {
      return;
    }

    this.showErrorMessage = false;
    this.addressValidated = false;
    this.errorMessage = null;
    this.multipleAddressDetails = null;
    if (this.addressDetails.streetTypeId === 0) {
      this.addressDetails.streetType = '';
    }
    this.isRequesting = true;
    this.addressService.validateAddress(this.addressDetails, this.loginService.getToken()).subscribe(
      (addresses: LocationDetails[]) => {
        this.isRequesting = false;
        if (addresses == null) {
          this.errorMessage = 'Application is unavailable';
        } else if (addresses.length === 0) {
          this.errorMessage = 'Address not found';
        } else {
          // single result returned
          if (addresses.length === 1) {
            this.setValidatedAddress(addresses[0]);
            this.errorMessage = null;
          } else if (addresses.length > 1) { // multiple results
            // more than max number of records - refine search criteria
            if (addresses.length > this.maxNumberOfAddresses) {
              this.errorMessage = 'Too many records found, please review your search criteria';
            } else { // less than maxNumberOfAddresses
              this.multipleAddressDetails = addresses;
              this.multipleAddresses.items = AddressDetailsComponent.addressesToDataItems(addresses);
              this.multipleAddresses.selectedItemId = 0;
              this.errorMessage = null;
            }
          }
        }
      },
      err => {
        // Report errors if any
        this.isRequesting = false;
        if (err.status === 404) {
          this.errorMessage = err.message.err;
        } else if (err.status === 0 && err.statusText === 'Unknown Error') {
          this.errorMessage = 'Application is unavailable';
        } else {
          this.errorMessage = 'Server error';
        }
      }
    );
  }

  displayErrorMessage(): string {
    this.errorMessage = null;
    if (this.invalid) {
      const errorMessage = 'Please select valid address.';
      this.errorMessage = errorMessage;
      return errorMessage;
    }
  }
  displayPlanErrorMessage(): string {
    this.errorMessage = null;
    if (!this.addressHasPlan) {
      const errorMessage = 'A Plan of Subdivision number is required in order to submit an application. ' +
        'If you do not have a Plan of Subdivision number please email designs@gww.com.au.';
      this.errorMessage = errorMessage;
      return errorMessage;
    }
  }

  displayAddressMandatoryErrorMessage(): string {
    this.errorMessage = null;
    if (!this.addressHasStreetNo || !this.addressHasStreetName || !this.addressHasSuburb) {
      const errorMessage = 'A Street Number, Street Name and Suburb are required in order to submit an application. ';
      this.errorMessage = errorMessage;
      return errorMessage;
    }
  }

  getOnStyleForMultipleAddresses(): string {
    return (this.multipleAddressDetails != null && this.multipleAddressDetails.length > 0) ? 'block' : 'none';
  }

  //#REGION GET SET VARS
  get addressPopulated(): boolean {
    return this.addressHasValues && !this.addressInvalid;
  }

  get invalid(): boolean {
    return !this.addressPopulated || !this.addressValidated;
  }
  get invalidPlan(): boolean {
    return !this.addressHasPlan;
  }
  get invalidStreetName(): boolean {
    return !this.addressHasStreetName;
  }
  get invalidStreetNo(): boolean {
    return !this.addressHasStreetNo;
  }
  get invalidStreetType(): boolean {
    return !this.addressHasStreetType;
  }
  get invalidSuburb(): boolean {
    return !this.addressHasSuburb;
  }

  get errorMessageVisible(): boolean {
    return this.showErrorMessage || (this.addressInvalid && this.addressHasValues);
  }

  get IsRecycledWaterZone(): boolean {
    return this.addressDetails.recycledWaterZone && this.addressDetails.recycledWaterZone !== '';
  }

  get showErrorMessage(): boolean {
    return this.addressViewModel.showErrorMessage;
  }

  set showErrorMessage(v: boolean) {
    this.addressViewModel.showErrorMessage = v;
  }

  get addressInvalid(): boolean {
    return this.addressViewModel.addressInvalid;
  }

  set addressInvalid(v: boolean) {
    this.addressViewModel.addressInvalid = v;
  }

  get addressValidated(): boolean {
    return this.addressViewModel.addressValidated;
  }

  set addressValidated(v: boolean) {
    this.addressViewModel.addressValidated = v;
  }

  get addressDetails(): LocationDetails {
    return this.addressViewModel.addressDetails;
  }

  set addressDetails(v: LocationDetails) {
    this.addressViewModel.addressDetails = v;
  }

  get multipleAddressDetails(): LocationDetails[] {
    return this.addressViewModel.multipleAddressDetails;
  }

  set multipleAddressDetails(v: LocationDetails[]) {
    this.addressViewModel.multipleAddressDetails = v;
  }

  get errorMessage(): string {
    return this.addressViewModel.errorMessage;
  }

  set errorMessage(v: string) {
    this.addressViewModel.errorMessage = v;
  }

  get addressHasValues(): boolean {
    return this.addressViewModel.addressHasValues;
  }

  set addressHasValues(v: boolean) {
    this.addressViewModel.addressHasValues = v;
  }
  get addressHasPlan() {
    return this.addressViewModel.addressHasPlan;
  }
  set addressHasPlan(v: boolean) {
    this.addressViewModel.addressHasPlan = v;
  }

  get addressHasStreetName() {
    return this.addressViewModel.addressHasStreetName;
  }
  set addressHasStreetName(v: boolean) {
    this.addressViewModel.addressHasStreetName = v;
  }
  get addressHasStreetNo() {
    return this.addressViewModel.addressHasStreetNo;
  }
  set addressHasStreetNo(v: boolean) {
    this.addressViewModel.addressHasStreetNo = v;
  }
  get addressHasStreetType() {
    return this.addressViewModel.addressHasStreetType;
  }
  set addressHasStreetType(v: boolean) {
    this.addressViewModel.addressHasStreetType = v;
  }
  get addressHasSuburb() {
    return this.addressViewModel.addressHasSuburb;
  }
  set addressHasSuburb(v: boolean) {
    this.addressViewModel.addressHasSuburb = v;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/address-details/address-details.component.ts